import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import PriceCalculatorModal from "@/components/Modals/PriceCalculatorModal";
import PriceBreakdownModal from "@/components/Modals/PriceBreakdownModal";
import Modal from "@/components/Modals/Modal";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { DateTime } from "luxon";

// Parses a UTC datetime value that can be either SQL format ("2026-03-26 23:30:00")
// or ISO format ("2026-03-26T23:30") and returns a Luxon DateTime in the event's timezone.
function parseUTCValue(val, tz) {
  if (!val) return null;
  let dt = DateTime.fromSQL(val, { zone: "utc" });
  if (dt.isValid) return dt.setZone(tz);
  dt = DateTime.fromISO(val, { zone: "utc" });
  return dt.isValid ? dt.setZone(tz) : null;
}

const QUICK_JUMPS = [30,60, 120, 180, 240, 360];

function MinutesOffsetStepper({ label, description, value, onChange, eventStart, timezone, minOffset = 5, required = false, disabled = false }) {
  const tz = timezone || DEFAULT_TIMEZONE;

  // startDT: event start in local timezone, parsed from UTC SQL string
  const startDT = useMemo(() => parseUTCDate(eventStart, tz), [eventStart, tz]);

  // valueDT: current value in local timezone, parsed from UTC string (SQL or ISO)
  const valueDT = useMemo(() => parseUTCValue(value, tz), [value, tz]);

  const offsetMinutes = useMemo(() => {
    if (!valueDT || !startDT) return null;
    return Math.round(valueDT.diff(startDT, "minutes").minutes);
  }, [valueDT, startDT]);

  const computedDisplay = useMemo(() => {
    if (!valueDT) return null;
    return valueDT.toFormat("dd/MM/yyyy HH:mm");
  }, [valueDT]);

  const formatOffset = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `+${m}min`;
    if (m === 0) return `+${h}h`;
    return `+${h}h ${m}min`;
  };

  const handleActivate = () => {
    if (!startDT) return;
    const activationOffset = Math.max(30, minOffset);
    // Convert to UTC before sending — backend (Lambda) is UTC
    const utcDT = startDT.plus({ minutes: activationOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  const handleStep = (delta) => {
    if (offsetMinutes === null || !startDT) return;
    const newOffset = offsetMinutes + delta;
    if (newOffset < minOffset) return;
    // Convert to UTC before sending
    const utcDT = startDT.plus({ minutes: newOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  // Long press acceleration
  const holdRef = useRef(null);

  const startHold = (direction) => {
    if (offsetMinutes === null || !startDT) return;
    const startTime = Date.now();
    let acc = offsetMinutes;
    holdRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = elapsed > 3000 ? 60 : elapsed > 1500 ? 15 : 5;
      acc += direction * step;
      if (acc < minOffset) { acc = minOffset; return; }
      onChange(startDT.plus({ minutes: acc }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
    }, 150);
  };

  const stopHold = () => {
    clearInterval(holdRef.current);
    holdRef.current = null;
  };

  return (
    <div>
      <label className="block text-gray-300 text-xs font-semibold mb-0.5">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
      <p className="text-gray-500 text-xs mb-2">{description}</p>
      {offsetMinutes === null ? (
        <div className="flex items-center justify-between bg-aw-bluedark-95 border border-gray-600 rounded-lg px-3 py-2">
          <span className="text-gray-500 text-sm italic">Sin límite configurado</span>
          {!disabled && (
            <button
              type="button"
              onClick={handleActivate}
              disabled={!startDT}
              className="text-aw-green text-xs font-semibold hover:underline disabled:opacity-40"
            >
              Configurar
            </button>
          )}
        </div>
      ) : (
        <div className={`bg-aw-bluedark-95 border border-gray-600 rounded-lg px-3 py-2 space-y-1 ${disabled ? "opacity-60" : ""}`}>
          {/* Quick jump chips */}
          {!disabled && (
            <div className="flex gap-1 flex-wrap justify-center pb-1">
              {QUICK_JUMPS.filter((m) => m >= minOffset).map((mins) => {
                const isActive = offsetMinutes === mins;
                return (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => {
                      if (!startDT) return;
                      onChange(startDT.plus({ minutes: mins }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
                    }}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-aw-green text-aw-bluedark"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    +{mins / 60}h
                  </button>
                );
              })}
            </div>
          )}

          {/* +/- stepper with long press */}
          <div className="flex items-center gap-2">
            {!disabled && (
              <button
                type="button"
                onClick={() => handleStep(-5)}
                onMouseDown={() => startHold(-1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={() => startHold(-1)}
                onTouchEnd={stopHold}
                disabled={offsetMinutes - 5 < minOffset}
                className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center disabled:opacity-40 hover:bg-gray-600 select-none"
              >
                −
              </button>
            )}
            <span className="text-white text-sm font-semibold flex-1 text-center">
              {formatOffset(offsetMinutes)} desde el inicio
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleStep(5)}
                onMouseDown={() => startHold(1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={() => startHold(1)}
                onTouchEnd={stopHold}
                className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center hover:bg-gray-600 select-none"
              >
                +
              </button>
            )}
          </div>
          {computedDisplay && (
            <p className="text-aw-green/80 text-xs text-center">{computedDisplay}</p>
          )}
          {!required && !disabled && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-red-400 text-xs hover:underline"
              >
                Quitar límite
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TicketFormCard({
  ticket,
  onUpdate,
  onRemove,
  eventImage,
  dragHandleProps,
  validateTicketQuantity,
  eventAforo = 0,
  eventInfo = {}, // Información del evento incluyendo comisiones
  infoEmpresa = {},
}) {
  console.log("eventInfo in tipoTicket:", eventInfo);
  console.log("3 - initialDataTickets:", ticket);
  console.log("4 - initialDataTickets:", ticket.invitacionHoraLimiteDescuento);
  const [isExpanded, setIsExpanded] = useState(!ticket.id);
  const [imagePreview, setImagePreview] = useState(
    ticket.customImagePreview || null,
  );
  const fileInputRef = useRef(null);

  const navigate = useNavigate();

  // Estados para los modales de precio
  const [showPriceCalculator, setShowPriceCalculator] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Efecto para sincronizar imagePreview cuando cambia ticket.customImagePreview
  useEffect(() => {
    console.log(
      "TicketFormCard useEffect - Ticket:",
      ticket.id,
      "useEventImage:",
      ticket.useEventImage,
      "customImagePreview:",
      ticket.customImagePreview,
    );

    // Solo actualizar si no hay preview local y hay un customImagePreview del ticket
    if (!imagePreview && ticket.customImagePreview) {
      console.log(
        "Estableciendo imagePreview desde ticket.customImagePreview:",
        ticket.customImagePreview,
      );
      setImagePreview(ticket.customImagePreview);
    }
    // Si se cambia a usar imagen del evento, limpiar el preview local
    if (ticket.useEventImage && imagePreview) {
      console.log("Limpiando imagePreview porque se activó useEventImage");
      setImagePreview(null);
    }
  }, [ticket.customImagePreview, ticket.useEventImage, imagePreview]);

  // Ajustar descuento de invitación si el precio baja por debajo o igual al descuento
  useEffect(() => {
    if (
      ticket.esInvitacion &&
      ticket.price > 0 &&
      ticket.invitacionDescuento !== null &&
      ticket.invitacionDescuento !== undefined &&
      ticket.invitacionDescuento >= ticket.price
    ) {
      onUpdate({ ...ticket, invitacionDescuento: ticket.price - 1 });
    }
  }, [ticket.price]);

  const handleFieldChange = (field, value) => {
    console.log(`Updating ${field} to:`, value);
    onUpdate({ ...ticket, [field]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    console.log("Archivo seleccionado:", file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        console.log(
          "Actualizando ticket con archivo:",
          file.name,
          "Tamaño:",
          file.size,
        );

        // Actualizar todo en una sola operación para evitar conflictos
        const updatedTicket = {
          ...ticket,
          customImage: file,
          customImagePreview: reader.result,
          useEventImage: false,
        };

        console.log("Ticket actualizado con imagen:", {
          id: ticket.id,
          hasCustomImage: updatedTicket.customImage instanceof File,
          fileName: updatedTicket.customImage?.name,
          useEventImage: updatedTicket.useEventImage,
        });

        onUpdate(updatedTicket);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUseEventImage = (checked) => {
    console.log("Cambiando useEventImage a:", checked);

    if (checked) {
      // Limpiar imagen personalizada cuando se activa "usar imagen del evento"
      setImagePreview(null);
      const updatedTicket = {
        ...ticket,
        useEventImage: true,
        customImage: null,
        customImagePreview: null,
      };

      console.log(
        "Ticket actualizado para usar imagen del evento:",
        updatedTicket,
      );
      onUpdate(updatedTicket);
    } else {
      // Solo cambiar el flag cuando se desactiva
      const updatedTicket = {
        ...ticket,
        useEventImage: false,
      };

      console.log(
        "Ticket actualizado para NO usar imagen del evento:",
        updatedTicket,
      );
      onUpdate(updatedTicket);
    }
  };

  // Handler para guardar precio desde la calculadora
  const handleSavePrice = ({
    precio,
    precioSinIva,
    utilidadNeta,
    utilidadNetaConIva,
    comisionAW,
    comisionAWConIva,
    comisionAmbassador,
    comisionAmbassadorConIva,
  }) => {
    console.log("Guardando precio:", {
      precio,
      precioSinIva,
      utilidadNeta,
      utilidadNetaConIva,
      comisionAW,
      comisionAWConIva,
      comisionAmbassador,
      comisionAmbassadorConIva,
    });
    onUpdate({
      ...ticket,
      price: precio, // Precio final con IVA
      precioSinIva,
      utilidadNeta,
      utilidadNetaConIva,
      comisionAW,
      comisionAWConIva,
      comisionAmbassador,
      comisionAmbassadorConIva,
    });
  };

  const displayImage = ticket.useEventImage
    ? eventImage
    : ticket.customImagePreview || imagePreview;

  // Determinar si es un ticket nuevo o existente
  const isNewTicket =
    typeof ticket.id === "string" && ticket.id.startsWith("temp-");
  const ticketLabel = isNewTicket ? "Nuevo Ticket" : `Ticket`;

  // Cuando el evento está publicado, los tickets existentes (con id numérico) son inmutables
  const isReadOnly = eventInfo?.published == 1 && !isNewTicket;

  // Debug: mostrar el estado actual del ticket
  console.log("Ticket state:", {
    id: ticket.id,
    isNew: isNewTicket,
    scope: ticket.scope,
    groupSize: ticket.groupSize,
  });

  return (
    <Motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-aw-bluedark rounded-xl p-4 mb-3 border border-aw-green"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-aw-green transition"
          >
            <FontAwesomeIcon
              icon="fa-solid fa-grip-vertical"
              className="text-lg"
            />
          </div>

          {/* Ticket Preview */}
          {displayImage && (
            <img
              src={displayImage}
              alt="Ticket preview"
              className="w-12 h-12 rounded-lg object-cover bg-aw-bluegray/35"
            />
          )}

          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-gray-400 text-xs">
                <span className="text-aw-green">
                  {ticket.quantity || 1}{" "}
                  {ticket.quantity > 1 ? "tickets" : "ticket"}
                </span>{" "}
                disponibles a la venta
              </p>
              {isNewTicket && (
                <span className="bg-aw-green text-aw-bluedark text-xs px-2 py-0.5 rounded-full font-bold">
                  NUEVO
                </span>
              )}
              {isReadOnly && (
                <span className="bg-gray-700 text-gray-400 text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                  <FontAwesomeIcon icon="fa-solid fa-lock" className="text-xs" />
                  Solo lectura
                </span>
              )}
            </div>
            <p className="text-white font-bold text-sm">{ticketLabel}</p>
            <p className="text-white font-bold text-sm">
              {ticket.customTypeName || "- - - - - - - - - -"}
            </p>
            <div className="text-aw-green font-bold text-lg">
              <span className="text-aw-green font-bold text-lg block">
                {/* Mostrar el precio en tipo de moneda chilena CLP */}
                {ticket.price
                  ? "CLP " +
                    new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                    }).format(ticket.price)
                  : "CLP $ ---.---"}
              </span>

              {/* Sección de alcance */}
              <span className="text-gray-400 font-normal text-xs block">
                {ticket.scope === "individual"
                  ? "/ por persona"
                  : "/ por grupo de " + (ticket.groupSize || 2) + " personas"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-tour-id="ticket-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-aw-green hover:text-white transition p-2"
          >
            <FontAwesomeIcon
              icon={
                isExpanded
                  ? "fa-solid fa-chevron-up"
                  : "fa-solid fa-chevron-down"
              }
            />
          </button>
          {(eventInfo?.published == 0 || isNewTicket) && (
            <button
              onClick={onRemove}
              className="text-red-400 hover:text-red-300 transition p-2"
            >
              <FontAwesomeIcon icon="fa-solid fa-trash" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Form */}
      <Motion.div
        initial={false}
        animate={{
          height: isExpanded ? "auto" : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="space-y-4 pt-3 border-t border-gray-700">
          {/* Nombre del Tipo */}
          <div>
            <label className="block text-white text-sm font-semibold mb-2">
              Nombre del Tipo
            </label>
            {!isReadOnly ? (
              <input
                type="text"
                value={ticket.customTypeName || ""}
                onChange={(e) =>
                  handleFieldChange("customTypeName", e.target.value)
                }
                placeholder="Ej: VIP, General, Estudiante..."
                className="w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-aw-green focus:outline-none"
              />
            ) : (
              <p className="w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-aw-green focus:outline-none cursor-not-allowed hover:bg-aw-bluegray/35 duration-300">
                {ticket.customTypeName || "- - - - - - - - - -"}
              </p>
            )}
          </div>

          {/* Scope (Individual vs Group) TODO: Habilitar cuando tengamos resuelto el SoldOut para tickets grupales */}
          {/* <div>
            <label className="block text-white text-sm font-semibold mb-2">
              Alcance del Ticket
            </label>

            *-* Switch Toggle *-*
            <div className="flex items-center gap-3 mb-3">
              <span
                className={`text-sm font-medium ${
                  ticket.scope === "individual"
                    ? "text-aw-green"
                    : "text-gray-400"
                }`}
              >
                Individual
              </span>

              *-* Custom Switch *-*
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={ticket.scope === "group"}
                  onChange={(e) => {
                    const isGroup = e.target.checked;
                    console.log(
                      "Toggle switch:",
                      isGroup ? "Group" : "Individual"
                    );
                    console.log("Current ticket state before toggle:", {
                      scope: ticket.scope,
                      ticketGrupal: ticket.ticketGrupal,
                      groupSize: ticket.groupSize,
                    });

                    // Crear el nuevo ticket con todos los campos actualizados
                    const updatedTicket = {
                      ...ticket,
                      scope: isGroup ? "group" : "individual",
                      ticketGrupal: isGroup,
                      groupSize: isGroup
                        ? Math.max(2, ticket.groupSize || 2)
                        : 1,
                    };

                    console.log(
                      "Updated ticket state after toggle:",
                      updatedTicket
                    );

                    // Usar onUpdate directamente con el ticket completo
                    onUpdate(updatedTicket);
                  }}
                  className="sr-only"
                />
                <div className="relative">
                  <div
                    className={`block w-14 h-8 rounded-full transition-colors duration-300 ${
                      ticket.scope === "group" ? "bg-aw-green" : "bg-gray-600"
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-300 ${
                      ticket.scope === "group" ? "translate-x-6" : ""
                    }`}
                  ></div>
                </div>
              </label>

              <span
                className={`text-sm font-medium ${
                  ticket.scope === "group" ? "text-aw-green" : "text-gray-400"
                }`}
              >
                Grupal
              </span>
            </div>

            *-* Descripción del estado actual *-*
            <p className="text-xs text-gray-400 mb-3">
              {ticket.scope === "individual"
                ? "Este ticket es para una persona individual"
                : `Este ticket es para un grupo de ${
                    ticket.groupSize || 2
                  } personas`}
            </p>

            *-* Input de cantidad de personas solo cuando es grupal *-*
            {ticket.scope === "group" && (
              <div className="bg-aw-green/15 rounded-2xl p-2">
                <label className="block text-gray-200 text-sm mb-2">
                  Cantidad de personas por ticket *
                </label>
                <input
                  type="number"
                  min="2"
                  value={ticket.groupSize || 2}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value) || 2;
                    const finalSize = Math.max(2, newSize); // Mínimo 2 personas

                    // Actualizar el groupSize y revalidar
                    const updatedTicket = {
                      ...ticket,
                      groupSize: finalSize,
                      cantidadPersonasTicketGrupal: finalSize,
                    };

                    // Usar onUpdate directamente con el ticket completo para revalidar
                    onUpdate(updatedTicket);
                  }}
                  className="w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-aw-green focus:outline-none"
                  required
                />
                <p className="text-xs text-right text-gray-400 mt-1">
                  Mínimo 2 personas para tickets grupales
                </p>
              </div>
            )}
          </div> */}

          <div className="flex flex-col sm:flex-row sm:justify-center sm:gap-x-4 gap-y-4">
            {/* How many tickets on this type? */}
            <div>
              <label className="block text-white text-sm font-semibold mb-2">
                Cantidad de tickets a vender de este tipo *
                {eventAforo > 0 && (
                  <span className="text-gray-400 text-xs ml-2">
                    {ticket.scope === "group" || ticket.ticketGrupal
                      ? `(Cada ticket ocupa ${
                          ticket.groupSize ||
                          ticket.cantidadPersonasTicketGrupal ||
                          1
                        } espacios del aforo)`
                      : `(Máx. aforo: ${eventAforo})`}
                  </span>
                )}
              </label>
              {!isReadOnly ? (
                <input
                  type="number"
                  min="1"
                  value={ticket.quantity || 1}
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;

                    // Validar si la nueva cantidad es válida
                    if (
                      validateTicketQuantity &&
                      !validateTicketQuantity(newQuantity, ticket.id)
                    ) {
                      // Si no es válida, no actualizar pero mostrar feedback visual
                      return;
                    }

                    const updatedFields = { quantity: newQuantity };
                    if (
                      ticket.invitacionCupoGratuito !== null &&
                      ticket.invitacionCupoGratuito !== undefined &&
                      ticket.invitacionCupoGratuito > newQuantity
                    ) {
                      updatedFields.invitacionCupoGratuito = newQuantity;
                    }
                    onUpdate({ ...ticket, ...updatedFields });
                  }}
                  className={`w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border focus:outline-none transition-colors ${
                    validateTicketQuantity &&
                    !validateTicketQuantity(ticket.quantity || 1, ticket.id)
                      ? "border-red-500 focus:border-red-400"
                      : "border-gray-600 focus:border-aw-green"
                  }`}
                />
              ) : (
                <p className="w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-aw-green focus:outline-none cursor-not-allowed hover:bg-aw-bluegray/35 duration-300">
                  {ticket.quantity || 1}
                </p>
              )}
              {validateTicketQuantity &&
                !validateTicketQuantity(ticket.quantity || 1, ticket.id) && (
                  <p className="text-red-400 text-xs mt-1">
                    ⚠️ Cantidad excede el aforo disponible
                    {ticket.scope === "group" || ticket.ticketGrupal
                      ? ` (${ticket.quantity} tickets × ${
                          ticket.groupSize ||
                          ticket.cantidadPersonasTicketGrupal ||
                          1
                        } personas = ${
                          (ticket.quantity || 1) *
                          (ticket.groupSize ||
                            ticket.cantidadPersonasTicketGrupal ||
                            1)
                        } personas)`
                      : ""}
                  </p>
                )}
            </div>

            {/* Max Tickets Per User */}
            <div>
              <label className="block text-white text-sm font-semibold mb-2">
                Compra máxima por usuario *
              </label>
              {!isReadOnly ? (
                <input
                  type="number"
                  min="1"
                  value={ticket.maxPerUser || 1}
                  disabled={!!ticket.esInvitacion}
                  onChange={(e) => {
                    if (ticket.esInvitacion) return;
                    handleFieldChange(
                      "maxPerUser",
                      parseInt(e.target.value) || 1,
                    );
                  }}
                  className={`w-full rounded-lg px-4 py-2 border focus:outline-none transition-colors ${
                    ticket.esInvitacion
                      ? "bg-yellow-950 text-white cursor-not-allowed border-yellow-300"
                      : "bg-aw-bluedark-95 text-white border-gray-600 focus:border-aw-green"
                  }`}
                  required
                />
              ) : (
                <p className="w-full bg-aw-bluedark-95 text-white rounded-lg px-4 py-2 border border-gray-600 focus:border-aw-green focus:outline-none cursor-not-allowed hover:bg-aw-bluegray/35 duration-300">
                  {ticket.maxPerUser || 1}
                </p>
              )}
            </div>
          </div>

          {/* Price - Replaced with Calculator Button */}
          <div>
            <label className="block text-white text-sm font-semibold mb-2">
              Precio *
            </label>
            <div className="flex gap-2">
              {/* Botón para abrir calculadora */}
              <button
                type="button"
                data-tour-id="btn-definir-precio"
                onClick={() => {
                  if (!isReadOnly) {
                    setShowPriceCalculator(true);
                  }
                }}
                disabled={isReadOnly}
                className={`flex-1 group/btn rounded-lg px-4 py-2 border focus:outline-none transition-colors flex items-center justify-between ${
                  isReadOnly
                    ? "bg-aw-bluedark-95 text-gray-400 border-gray-600 cursor-not-allowed"
                    : "bg-aw-green text-aw-blueblack border-gray-600 hover:bg-aw-blueblack hover:text-aw-green hover:border-aw-green"
                }`}
              >
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon="fa-solid fa-calculator"
                    className={isReadOnly ? "text-gray-500" : "text-aw-blueblack group-hover/btn:text-aw-green"}
                  />
                  {ticket.price > 0 ? (
                    <span className="font-semibold">
                      {new Intl.NumberFormat("es-CL", {
                        style: "currency",
                        currency: "CLP",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(ticket.price)}
                    </span>
                  ) : (
                    <span className={isReadOnly ? "text-gray-500" : "text-aw-blueblack group-hover/btn:text-aw-green"}>
                      Definir precio
                    </span>
                  )}
                </span>
                <FontAwesomeIcon
                  icon="fa-solid fa-chevron-right"
                  className={isReadOnly ? "text-gray-600" : "text-gray-500 group-hover/btn:text-aw-green transition-colors"}
                />
              </button>

              {/* Botón para ver desglose (solo visible si hay precio) */}
              {ticket.price > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPriceBreakdown(true)}
                  className="bg-aw-bluedark-95 text-aw-green rounded-lg px-4 py-2 border border-gray-600 hover:border-aw-green focus:border-aw-green focus:outline-none transition-colors flex items-center gap-2"
                  title="Ver desglose de precio"
                >
                  <FontAwesomeIcon icon="fa-solid fa-chart-pie" />
                </button>
              )}
            </div>
          </div>

          {/* Toggle: Es tipo Invitacion */}
          <div className="border border-aw-green/30 rounded-xl p-4 bg-aw-green/5">
            <label className={`flex items-center gap-3 mb-2 ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={!!ticket.esInvitacion}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (isReadOnly) return;
                    const checked = e.target.checked;
                    // Si intenta activar y no tiene plan, mostrar modal
                    if (checked && !(infoEmpresa && infoEmpresa.planActivo)) {
                      setShowPlanModal(true);
                      return;
                    }
                    const updatedTicket = {
                      ...ticket,
                      esInvitacion: checked ? 1 : 0,
                      ...(checked && { maxPerUser: 1 }),
                    };
                    if (!checked) {
                      updatedTicket.invitacionCupoGratuito = null;
                      updatedTicket.invitacionHoraLimiteGratuita = null;
                      updatedTicket.invitacionDescuento = null;
                      updatedTicket.invitacionHoraLimiteDescuento = null;
                    }
                    onUpdate(updatedTicket);
                  }}
                  className="sr-only"
                />
                <div
                  className={`block w-12 h-6 rounded-full transition-colors duration-300 ${
                    ticket.esInvitacion ? "bg-aw-green" : "bg-gray-600"
                  }`}
                ></div>
                <div
                  className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 ${
                    ticket.esInvitacion ? "translate-x-6" : ""
                  }`}
                ></div>
              </div>
              <span className="text-white text-sm font-semibold flex items-center gap-2">
                Habilitar postulaciones
                {!(infoEmpresa && infoEmpresa.planActivo) && (
                  <FontAwesomeIcon
                    icon="fa-solid fa-star"
                    className="text-aw-green text-xs"
                    title="Función Premium"
                  />
                )}
              </span>
            </label>

            {ticket.esInvitacion == true && (
              <>
                <p className="text-yellow-400 text-xs mb-3 flex items-start gap-2">
                  <FontAwesomeIcon
                    icon="fa-solid fa-triangle-exclamation"
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    Este tipo de ticket solo podrá ser obtenido por el usuario al postularse y tú como empresa lo aceptes. El <b>Descuento Fijo es obligatorio</b>; los demás campos son opcionales.
                  </span>
                </p>

                <div className="flex flex-col gap-4">
                  {/* Descuento fijo — REQUERIDO */}
                  <div>
                    <label className="block text-gray-300 text-xs font-semibold mb-0.5">
                      Descuento fijo (CLP) <span className="text-red-400">*</span>
                    </label>
                    <p className="text-gray-500 text-xs mb-1">
                      Monto que se descontará del precio original al aceptar la solicitud. Define cuánto pagará el usuario invitado.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        max={ticket.price ? ticket.price - 1 : undefined}
                        disabled={!ticket.price || isReadOnly}
                        value={ticket.invitacionDescuento ?? ""}
                        onChange={(e) => {
                          if (!ticket.price || isReadOnly) return;
                          const raw = e.target.value === "" ? null : parseInt(e.target.value);
                          const capped = raw !== null && raw >= ticket.price ? ticket.price - 1 : raw;
                          // mínimo 1
                          const final = capped !== null && capped < 1 ? 1 : capped;
                          handleFieldChange("invitacionDescuento", final);
                        }}
                        placeholder={ticket.price ? "Requerido" : "—"}
                        className={`flex-1 bg-aw-bluedark-95 text-white rounded-lg px-3 py-2 border focus:outline-none text-sm transition-colors ${
                          !ticket.price || isReadOnly
                            ? "border-gray-700 opacity-50 cursor-not-allowed"
                            : (!ticket.invitacionDescuento || ticket.invitacionDescuento < 1)
                            ? "border-red-500 focus:border-red-400"
                            : "border-gray-600 focus:border-aw-green"
                        }`}
                      />
                      {ticket.price > 0 && ticket.invitacionDescuento > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowDiscountBreakdown(true)}
                          className="bg-aw-bluedark-95 text-aw-green rounded-lg px-3 py-2 border border-gray-600 hover:border-aw-green focus:outline-none transition-colors flex items-center gap-1 text-xs"
                          title="Ver desglose con descuento"
                        >
                          <FontAwesomeIcon icon="fa-solid fa-chart-pie" />
                        </button>
                      )}
                    </div>
                    {!ticket.price && (
                      <p className="text-yellow-400 text-xs mt-1">
                        Define un precio antes de configurar el descuento.
                      </p>
                    )}
                    {ticket.price > 0 && (!ticket.invitacionDescuento || ticket.invitacionDescuento < 1) && (
                      <p className="text-red-400 text-xs mt-1">
                        Este campo es obligatorio para tickets de invitación.
                      </p>
                    )}
                  </div>

                  {/* Cupo gratuito */}
                  <div>
                    <label className="block text-gray-300 text-xs font-semibold mb-0.5">
                      Cupo gratuito (N primeras entradas)
                    </label>
                    <p className="text-gray-500 text-xs mb-1">
                      Las primeras N solicitudes aceptadas obtendrán su entrada sin costo, ignorando el descuento fijo.
                    </p>
                    <input
                      type="number"
                      min="0"
                      max={ticket.quantity}
                      disabled={isReadOnly}
                      value={ticket.invitacionCupoGratuito ?? ""}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        const raw = e.target.value === "" ? null : parseInt(e.target.value);
                        const capped = raw !== null && raw > (ticket.quantity || 0)
                          ? (ticket.quantity || 0)
                          : raw;
                        handleFieldChange("invitacionCupoGratuito", capped);
                      }}
                      placeholder="Opcional"
                      className={`w-full bg-aw-bluedark-95 text-white rounded-lg px-3 py-2 border focus:outline-none text-sm ${isReadOnly ? "border-gray-700 opacity-50 cursor-not-allowed" : "border-gray-600 focus:border-aw-green"}`}
                    />
                  </div>

                  {/* Hora limite gratuita */}
                  <MinutesOffsetStepper
                    label="Hora límite entrada gratuita"
                    description="Cuánto tiempo después del inicio del evento se puede recibir una entrada del cupo gratuito. Después de este momento se aplica el descuento fijo."
                    value={ticket.invitacionHoraLimiteGratuita ?? null}
                    onChange={(val) => {
                      const updatedFields = { invitacionHoraLimiteGratuita: val };
                      if (val && ticket.invitacionHoraLimiteDescuento) {
                        // Both values are UTC — compare them directly
                        const newGratuita = DateTime.fromISO(val, { zone: "utc" });
                        const descuento = parseUTCValue(ticket.invitacionHoraLimiteDescuento, "utc");
                        if (newGratuita.isValid && descuento && newGratuita > descuento) {
                          updatedFields.invitacionHoraLimiteDescuento = val;
                        }
                      }
                      onUpdate({ ...ticket, ...updatedFields });
                    }}
                    eventStart={eventInfo?.fechaHoraInicio}
                    timezone={eventInfo?.zonaHoraria}
                    minOffset={5}
                    required={true}
                    disabled={isReadOnly}
                  />
                  {!ticket.invitacionHoraLimiteGratuita && (
                    <p className="text-red-400 text-xs -mt-3">Este campo es obligatorio para tickets de invitación.</p>
                  )}

                  {/* Hora limite descuento */}
                  <MinutesOffsetStepper
                    label="Hora límite por descuento"
                    description="Cuánto tiempo después del inicio del evento se aplica el descuento fijo. Solicitudes aceptadas después de este momento pagarán el precio completo."
                    value={ticket.invitacionHoraLimiteDescuento ?? null}
                    onChange={(val) => handleFieldChange("invitacionHoraLimiteDescuento", val)}
                    eventStart={eventInfo?.fechaHoraInicio}
                    timezone={eventInfo?.zonaHoraria}
                    minOffset={(() => {
                      if (!ticket.invitacionHoraLimiteGratuita || !eventInfo?.fechaHoraInicio) return 5;
                      const tz = eventInfo?.zonaHoraria || DEFAULT_TIMEZONE;
                      const startDT = parseUTCDate(eventInfo.fechaHoraInicio, tz);
                      if (!startDT) return 5;
                      // Gratuita is stored as UTC — parse then convert to local to diff
                      const gratuita = parseUTCValue(ticket.invitacionHoraLimiteGratuita, tz);
                      if (!gratuita) return 5;
                      return Math.max(5, Math.round(gratuita.diff(startDT, "minutes").minutes));
                    })()}
                    required={true}
                    disabled={isReadOnly}
                  />
                  {!ticket.invitacionHoraLimiteDescuento && (
                    <p className="text-red-400 text-xs -mt-3">Este campo es obligatorio para tickets de invitación.</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Image Options */}
          <div>
            <label className="block text-white text-sm font-semibold mb-2">
              Imagen del Ticket
            </label>

            {/* Use Event Image Checkbox */}
            {!isReadOnly && (
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ticket.useEventImage}
                  onChange={(e) => handleUseEventImage(e.target.checked)}
                  className="w-4 h-4 accent-aw-green"
                />
                <span className="text-gray-300 text-sm">
                  Usar imagen del evento
                </span>
              </label>
            )}

            {/* Custom Image Upload */}
            {!ticket.useEventImage && !isReadOnly && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-aw-bluedark-95 text-gray-300 rounded-lg px-4 py-3 border border-gray-600 hover:border-aw-green transition text-sm flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon="fa-solid fa-upload" />
                  {imagePreview
                    ? "Cambiar imagen"
                    : "Subir imagen personalizada"}
                </button>
              </div>
            )}

            {/* Image Preview */}
            {displayImage && (
              <div className="mt-3">
                <img
                  src={displayImage}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg bg-aw-bluegray/35"
                />
              </div>
            )}
          </div>
        </div>
      </Motion.div>

      {/* Modales de Precio */}
      <PriceCalculatorModal
        isOpen={showPriceCalculator}
        onClose={() => setShowPriceCalculator(false)}
        onSave={handleSavePrice}
        initialBasePrice={ticket.utilidadNeta || 0}
        initialFinalPrice={ticket.price || 0}
        ambassadorCommission={eventInfo.ambassadorIncentive || 0}
      />

      <PriceBreakdownModal
        isOpen={showPriceBreakdown}
        onClose={() => setShowPriceBreakdown(false)}
        precio={ticket.price || 0}
        precioSinIva={ticket.precioSinIva || 0}
        utilidadNeta={ticket.utilidadNeta || 0}
        utilidadNetaConIva={ticket.utilidadNetaConIva || 0}
        comisionAW={ticket.comisionAW || 0}
        comisionAWConIva={ticket.comisionAWConIva || 0}
        comisionAmbassador={ticket.comisionAmbassador || 0}
        comisionAmbassadorConIva={ticket.comisionAmbassadorConIva || 0}
        ambassadorCommission={eventInfo.ambassadorIncentive || 0}
      />

      {/* Modal de desglose del precio con descuento */}
      {(() => {
        const precioConDescuento = (ticket.price || 0) - (ticket.invitacionDescuento || 0);
        const ambassadorPct = eventInfo?.ambassadorIncentive || 0;
        const totalComisionesPct = 10 + ambassadorPct;
        const utilidadNetaConIvaDesc = Math.round(precioConDescuento * ((100 - totalComisionesPct) / 100));
        const comisionAmbassadorConIvaDesc = ambassadorPct > 0 ? Math.round(precioConDescuento * (ambassadorPct / 100)) : 0;
        const comisionAWConIvaDesc = precioConDescuento - utilidadNetaConIvaDesc - comisionAmbassadorConIvaDesc;
        const precioSinIvaDesc = Math.round(precioConDescuento / 1.19);
        const utilidadNetaDesc = Math.round(utilidadNetaConIvaDesc / 1.19);
        const comisionAmbassadorDesc = ambassadorPct > 0 ? Math.round(comisionAmbassadorConIvaDesc / 1.19) : 0;
        const comisionAWDesc = precioSinIvaDesc - utilidadNetaDesc - comisionAmbassadorDesc;

        return (
          <PriceBreakdownModal
            isOpen={showDiscountBreakdown}
            onClose={() => setShowDiscountBreakdown(false)}
            precio={precioConDescuento}
            precioSinIva={precioSinIvaDesc}
            utilidadNeta={utilidadNetaDesc}
            utilidadNetaConIva={utilidadNetaConIvaDesc}
            comisionAW={comisionAWDesc}
            comisionAWConIva={comisionAWConIvaDesc}
            comisionAmbassador={comisionAmbassadorDesc}
            comisionAmbassadorConIva={comisionAmbassadorConIvaDesc}
            ambassadorCommission={ambassadorPct}
            title="Desglose con Descuento"
            subtitle="Composición del precio al aplicar el descuento de invitación"
          />
        );
      })()}

      {/* Modal informativo de plan */}
      {showPlanModal && (
        <Modal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          large="max-w-2xl"
        >
          <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin overflow-y-auto text-center">
            <FontAwesomeIcon
              icon="fa-solid fa-info-circle"
              className="text-5xl text-aw-green mb-4 mx-auto"
            />
            <h3 className="text-lg font-bold text-white mb-4">
              Necesitas un plan
            </h3>
            <p className="text-sm text-gray-300">
              Para usar los{" "}
              <b className="font-semibold text-aw-green">
                "Tickets tipo Invitación"
              </b>{" "}
              necesitas adquirir un plan.
            </p>
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  navigate(`/empresa/${infoEmpresa.uid}`);
                }}
                className="px-4 py-2 bg-aw-green text-aw-bluedark rounded font-bold"
              >
                Ir a Suscripción
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Motion.div>
  );
}
